import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DollarSign, Users, CalendarDays, TrendingUp, ShieldCheck,
  Ban, Star, Gift, Settings as SettingsIcon, ExternalLink, ReceiptText, FileText, Image as ImageIcon, IdCard, Eye, List as ListIcon } from "lucide-react";
import { drName, formatDateShort, formatDateTimeParts, statusLabel } from "@/lib/utils";
import DocViewerDialog from "@/components/DocViewerDialog";
import ErrorBoundary from "@/components/ErrorBoundary";

/**
 * Jours fériés Équateur — admin config, s'applique à tous les médecins.
 * Chaque médecin peut opter "Trabajo este día" côté /doctor/panel/availability.
 */
/**
 * Iter14 P5.3 (2026-02-27) — Test accounts preview + auto-flag apply.
 * Reset itself is intentionally NOT wired here — the admin must confirm to
 * the developer before we execute the destructive reset. See PRD backlog.
 */
function TestAccountsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const load = () => api.get("/admin/test-accounts/preview").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const buildResetPayload = ({ dryRun }) => ({
    heuristic_doctor_ids: [
      "251ac98a-554e-4ec7-b6ba-149aee5dd32c",
      "2296f8a5-13f6-42f3-9519-1e54003634d6",
      "1bf5f89e-ff7f-4ef1-89dc-673e8e025ae6",
      "ad9a2aa3-4693-4b23-9efc-a47d09b83f6e",
      "44d9b9fc-2c1a-4c41-9e01-d795f19c4a10",
      "02852bd8-c250-4d0d-976e-e2b24056206b",
      "7628abc2-e987-4dfb-86ff-2aa5d26195fb",
      "278db8fa-9978-430e-a97f-fc63bd638ecf",
      "f19c7ce3-195e-4f98-9ee4-9f9b3c4dad9e",
    ],
    extra_referral_codes: [],
    move_test_to_least_populated_city: true,
    reset_test_credits: true,
    reset_partner_ids: [],
    reset_partner_emails: ["frederic.peters@agendik.io", "agoodjobsasu@gmail.com"],
    dry_run: dryRun,
  });
  const applyAuto = async () => {
    if (!window.confirm("Marquer comme TEST tous les comptes dont l'email contient '+test' ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/test-accounts/apply-auto-flag");
      toast.success(`${r.data.flagged} compte(s) marqué(s) comme TEST`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  if (!data) return null;
  const totals = data.totals_to_reset || {};
  return (
    <div className="p-4 rounded-xl border border-purple-200 bg-purple-50/40" data-testid="test-accounts-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-slate-900">Comptes test</h3>
          <p className="text-xs text-slate-500">Cachés du site public, jamais d'email, jamais de commission, exclus des chiffres.</p>
        </div>
        <Button data-testid="apply-auto-flag-btn" onClick={applyAuto} disabled={busy}
                className="bg-purple-600 hover:bg-purple-700 text-white h-8 text-xs">
          Marquer +test
        </Button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
        <div className="p-2 rounded bg-white border border-slate-100">
          <div className="text-slate-500">Déjà TEST</div>
          <div className="font-semibold text-slate-900">{(data.flagged_already || []).length}</div>
        </div>
        <div className="p-2 rounded bg-white border border-slate-100">
          <div className="text-slate-500">Auto +test</div>
          <div className="font-semibold text-slate-900">{(data.auto_flag_plus_test || []).length}</div>
        </div>
        <div className="p-2 rounded bg-white border border-slate-100">
          <div className="text-slate-500">Heuristique</div>
          <div className="font-semibold text-slate-900">{(data.auto_flag_heuristic || []).length}</div>
        </div>
        <div className="p-2 rounded bg-white border border-slate-100">
          <div className="text-slate-500">Crédits test à zéro</div>
          <div className="font-semibold text-slate-900">${totals.test_affiliate_credits_amount_usd || 0}</div>
        </div>
      </div>
      {(data.auto_flag_heuristic || []).length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-slate-600 mb-1">Suggestions heuristiques (à cocher manuellement dans la liste des médecins) :</div>
          <ul className="text-xs space-y-0.5 max-h-40 overflow-y-auto">
            {(data.auto_flag_heuristic || []).map(d => (
              <li key={d.id} data-testid={`heuristic-${d.id}`} className="flex items-center justify-between gap-2">
                <span className="truncate"><span className="font-mono text-[10px] text-slate-400">{d.id.slice(0,8)}</span> · {d.full_name} · <span className="text-slate-500">{d.email}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-purple-100 flex flex-wrap gap-2">
        <Button size="sm" data-testid="test-reset-dry-run"
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await api.post("/admin/test-accounts/apply-reset", buildResetPayload({ dryRun: true }));
                    setDryResult(r.data);
                    toast.success("Simulation terminée — vérifiez ci-dessous");
                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  finally { setBusy(false); }
                }} disabled={busy}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
          Simuler (dry-run)
        </Button>
        <Button size="sm" data-testid="test-reset-execute"
                onClick={async () => {
                  if (!dryResult) return toast.error("Lance d'abord une simulation");
                  if (!confirm("EXÉCUTER le reset EN VRAI sur la production ?\n\nCeci va :\n• Flaguer 9 doctors comme test\n• Les déplacer vers Ambato\n• Supprimer 0 crédit test\n• Reset la balance socio referidor de Frederic Peters à $0\n\nIrréversible.")) return;
                  setBusy(true);
                  try {
                    const r = await api.post("/admin/test-accounts/apply-reset", buildResetPayload({ dryRun: false }));
                    setDryResult(r.data);
                    toast.success("Reset exécuté ✓");
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  finally { setBusy(false); }
                }} disabled={busy || !dryResult}
                className="bg-red-600 hover:bg-red-700 text-white text-xs disabled:opacity-40 disabled:cursor-not-allowed">
          Exécuter (irréversible)
        </Button>
        <Button size="sm" variant="outline" data-testid="export-invoices-csv"
                onClick={async () => {
                  const token = localStorage.getItem("agendik_token");
                  const yr = new Date().getFullYear(), mo = String(new Date().getMonth()+1).padStart(2,"0");
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/invoices.csv?year=${yr}&month=${mo}`;
                  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
                  const b = await r.blob();
                  const a = document.createElement("a"); a.href = URL.createObjectURL(b);
                  a.download = `agendik-factures-${yr}-${mo}.csv`; a.click(); URL.revokeObjectURL(a.href);
                }}>Exporter factures du mois (CSV)</Button>
        <Button size="sm" variant="outline" data-testid="export-payouts-csv"
                onClick={async () => {
                  const token = localStorage.getItem("agendik_token");
                  const yr = new Date().getFullYear(), mo = String(new Date().getMonth()+1).padStart(2,"0");
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/exports/payout-receipts.csv?year=${yr}&month=${mo}`;
                  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
                  const b = await r.blob();
                  const a = document.createElement("a"); a.href = URL.createObjectURL(b);
                  a.download = `agendik-releves-commission-${yr}-${mo}.csv`; a.click(); URL.revokeObjectURL(a.href);
                }}>Exporter relevés commission (CSV)</Button>
      </div>
      {dryResult && (
        <div className="mt-4 p-3 rounded-lg bg-white border border-slate-200 text-xs font-mono max-h-60 overflow-y-auto" data-testid="reset-result">
          <div className="font-semibold text-slate-700 mb-1">
            {dryResult.dry_run ? "Résultat simulation :" : "Résultat exécution :"}
          </div>
          <pre className="whitespace-pre-wrap text-slate-600">{JSON.stringify(dryResult.steps, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

// Iter14 P5.4 (2026-02-27): HolidaysAdmin fully removed. The two CSV export
// buttons that used to live at the bottom of that card have been relocated
// into TestAccountsCard so admins keep them accessible under "Paramètres".

// SENESCYT click handler — copies the number to clipboard synchronously
// (from user gesture) and immediately opens the official page in a new tab.
// No async request before window.open → no mobile popup blocker.
// Config-level constant so switching the official URL is a one-line change.
const SENESCYT_URL = "https://titulos-edusuperior.minedec.gob.ec/consulta-titulos-web/faces/vista/consulta/consulta.xhtml";
function openSenescyt(e, num) {
  e.preventDefault();
  // 1. Open first, still inside the click gesture (Chrome Android, Safari iOS OK)
  const w = window.open(SENESCYT_URL, "_blank", "noopener,noreferrer");
  // 2. Copy the number to clipboard as fallback (site does not accept URL params)
  if (num) {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(num).then(
          () => toast.success(`Numéro ${num} copié`),
          () => toast.info(`Collez ce numéro : ${num}`));
      } else {
        // Legacy fallback
        const ta = document.createElement("textarea");
        ta.value = num; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
        toast.success(`Numéro ${num} copié`);
      }
    } catch { toast.info(`Numéro : ${num}`); }
  }
  if (!w) toast.info("Ouvrez manuellement le site SENESCYT (popup bloquée)");
}

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card className="border-slate-200"><CardContent className="p-5">
      <div className={`w-10 h-10 rounded-xl ${tone} flex items-center justify-center mb-3`}><Icon size={18}/></div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </CardContent></Card>
  );
}

function PartnerPendingRow({ a, onDone }) {
  const [code, setCode] = useState(
    (a.name || "").normalize("NFKD").replace(/[^A-Z0-9]+/gi, "-").toUpperCase().replace(/^-|-$/g, "")
    + "-" + new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const approve = async () => {
    if (!code.trim()) return toast.error("Code requis");
    setBusy(true);
    try {
      const r = await api.post(`/admin/affiliates/${a.id}/approve-partner`, { code: code.trim() });
      toast.success(`Approuvé · code ${r.data.code} · flyer envoyé`); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    if (!window.confirm("Refuser cette demande ?")) return;
    try { await api.post(`/admin/affiliates/${a.id}/reject`); toast.success("Refusé"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <tr className="border-t border-slate-100">
      <td className="p-2"><div className="font-medium">{a.name}</div></td>
      <td className="p-2 text-xs"><div>{a.email}</div><div className="text-slate-500">{a.phone || "—"}</div></td>
      <td className="p-2 text-xs max-w-[180px] truncate" title={a.payment_method}>{a.payment_method}</td>
      <td className="p-2 text-xs text-slate-500">{a.created_at ? formatDateShort(a.created_at) : ""}</td>
      <td className="p-2">
        <div className="flex items-center gap-1 flex-wrap">
          <Input data-testid={`pending-code-${a.id}`} value={code} onChange={e => setCode(e.target.value.toUpperCase())}
                 className="h-7 text-xs font-mono w-40"/>
          <Button data-testid={`approve-partner-${a.id}`} size="sm" onClick={approve} disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs">Approuver</Button>
          <Button data-testid={`reject-partner-${a.id}`} size="sm" variant="outline" onClick={reject}
                  className="border-red-200 text-red-600 hover:bg-red-50 h-7 px-2 text-xs">Refuser</Button>
        </div>
      </td>
    </tr>
  );
}

function PartnerActiveRow({ a, onDone }) {
  const [editing, setEditing] = useState(false);
  // draft: local raw input (preserves user cursor); code is only normalized on OK.
  const [draft, setDraft] = useState(a.code || "");
  const [busy, setBusy] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [credits, setCredits] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutAmt, setPayoutAmt] = useState(a.accumulated_balance || 0);
  const [payoutNote, setPayoutNote] = useState("");
  const [payInfo, setPayInfo] = useState(null);
  const [copiedField, setCopiedField] = useState("");
  const copyToClipboard = (v, field) => {
    if (!v) return;
    navigator.clipboard.writeText(String(v));
    setCopiedField(field);
    setTimeout(() => setCopiedField(""), 1200);
  };
  const openPayout = async () => {
    setPayoutAmt(a.accumulated_balance || 0);
    setPayoutOpen(true);
    try {
      const r = await api.get(`/admin/affiliates/${a.id}/payment-info`);
      setPayInfo(r.data);
    } catch { setPayInfo({}); }
  };
  const save = async () => {
    // Normalize on OK click only (uppercase, drop invalid chars, max 20).
    const cleaned = (draft || "").toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 20);
    if (!cleaned) return toast.error("Código inválido");
    setBusy(true);
    try { await api.post(`/admin/affiliates/${a.id}/update-code`, { code: cleaned }); toast.success("Code modifié"); setEditing(false); setDraft(cleaned); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const resend = async () => {
    try { await api.post(`/admin/affiliates/${a.id}/resend-flyer`); toast.success("Flyer renvoyé"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const openCredits = async () => {
    setShowCredits(true);
    try { const r = await api.get(`/admin/affiliates/${a.id}/credits`); setCredits(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const recordPayout = async () => {
    const v = Number(payoutAmt);
    if (!(v > 0)) return toast.error("Montant > 0");
    if (v > (a.accumulated_balance || 0) + 0.001) return toast.error(`Solde disponible : $${a.accumulated_balance}`);
    if (!window.confirm(`Enregistrer un versement de $${v} à ${a.name} ?`)) return;
    try {
      const r = await api.post(`/admin/affiliates/${a.id}/record-payout`, { amount_usd: v, note: payoutNote });
      toast.success(`Payé · nouveau solde $${r.data.new_balance}`);
      setPayoutOpen(false); setPayoutNote(""); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <>
    <tr className="border-t border-slate-100">
      <td className="p-2">
        {editing ? (
          <div className="flex gap-1">
            <Input data-testid={`edit-code-${a.id}`} value={draft}
                   onChange={e => setDraft(e.target.value)}
                   onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(a.code || ""); setEditing(false); } }}
                   maxLength={20}
                   placeholder="A-Z, 0-9, tiret"
                   className="h-7 text-xs font-mono"/>
            <Button size="sm" onClick={save} disabled={busy} className="h-7 px-2 text-xs bg-blue-600">OK</Button>
            <Button size="sm" variant="outline" onClick={() => { setDraft(a.code || ""); setEditing(false); }} className="h-7 px-2 text-xs">×</Button>
          </div>
        ) : (
          <button data-testid={`code-${a.id}`} onClick={() => { setDraft(a.code || ""); setEditing(true); }} className="font-mono text-blue-700 hover:underline text-xs">{a.code}</button>
        )}
      </td>
      <td className="p-2 text-xs">{a.name} · <span className="text-slate-500">{a.email}</span></td>
      <td className="p-2 text-right" data-testid={`inscrits-${a.code}`}>{a.doctors_registered}</td>
      <td className="p-2 text-right" data-testid={`confirmes-${a.code}`}>{a.doctors_confirmed ?? a.doctors_active}</td>
      <td className="p-2 text-right font-semibold text-emerald-700" data-testid={`balance-${a.code}`}>${(a.accumulated_balance ?? 0).toFixed(2)}</td>
      <td className="p-2 text-right text-slate-500" data-testid={`paidout-${a.code}`}>${(a.total_paid_out ?? 0).toFixed(2)}</td>
      <td className="p-2 text-right">
        <div className="flex gap-1 justify-end flex-wrap">
          <Button data-testid={`credits-${a.id}`} size="sm" variant="outline" onClick={openCredits} className="h-7 px-2 text-xs">Historial</Button>
          <Button data-testid={`payout-${a.id}`} size="sm" onClick={openPayout}
                  disabled={(a.accumulated_balance || 0) <= 0}
                  className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40">
            Marcar pago hecho
          </Button>
          <Button data-testid={`resend-flyer-${a.id}`} size="sm" variant="outline" onClick={resend} className="h-7 px-2 text-xs">Renvoyer flyer</Button>
        </div>
      </td>
    </tr>

    {payoutOpen && (
      <tr><td colSpan={7} className="p-3 bg-emerald-50/40 border-t border-emerald-100">
        {payInfo && (() => {
          const complete = payInfo.bank_titular && payInfo.id_or_ruc
            && (payInfo.bank_name || payInfo.bank_name_other) && payInfo.account_type
            && payInfo.account_number && payInfo.holder_email && payInfo.holder_phone;
          const row = (label, val, field) => (
            <div className="flex items-center gap-1">
              <span className="text-slate-500">{label} :</span>
              <span className={field === "account_number" ? "font-mono" : ""}>{val || <em className="text-slate-400">—</em>}</span>
              {val && (
                <button data-testid={`copy-${field}-${a.id}`} onClick={() => copyToClipboard(val, field)}
                        className="ml-1 text-blue-600 hover:text-blue-800 text-[10px] underline">
                  {copiedField === field ? "✓" : "copiar"}
                </button>
              )}
            </div>
          );
          return (
            <div data-testid={`payinfo-${a.id}`} className="mb-3 p-2 rounded bg-white border border-slate-200 text-[11px] text-slate-700">
              {!complete && (
                <div className="mb-2 text-amber-700 font-semibold">Datos bancarios incompletos</div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {row("Titular", payInfo.bank_titular, "bank_titular")}
                {row("Cédula", payInfo.id_or_ruc, "id_or_ruc")}
                {row("Banco", payInfo.bank_name_other || payInfo.bank_name, "bank_name")}
                {row("Tipo cuenta", payInfo.account_type, "account_type")}
                <div className="sm:col-span-2">{row("N° cuenta", payInfo.account_number, "account_number")}</div>
                {row("Email titular", payInfo.holder_email, "holder_email")}
                {row("Teléfono titular", payInfo.holder_phone, "holder_phone")}
              </div>
            </div>
          );
        })()}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-emerald-800">Enregistrer paiement à {a.name}</span>
          <Input data-testid={`payout-amount-${a.id}`} type="number" min="0" step="0.01" value={payoutAmt}
                 onChange={e => setPayoutAmt(e.target.value)} className="h-7 text-xs w-28"/>
          <Input data-testid={`payout-note-${a.id}`} placeholder="Note (Wise ref, banque…)" value={payoutNote}
                 onChange={e => setPayoutNote(e.target.value)} className="h-7 text-xs w-64"/>
          <Button data-testid={`payout-confirm-${a.id}`} size="sm" onClick={recordPayout} className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700">Confirmar</Button>
          <Button size="sm" variant="outline" onClick={() => setPayoutOpen(false)} className="h-7 px-2 text-xs">Annuler</Button>
          <span className="text-[11px] text-slate-500 ml-auto">Solde disponible : ${(a.accumulated_balance ?? 0).toFixed(2)}</span>
        </div>
      </td></tr>
    )}

    {showCredits && (
      <tr><td colSpan={7} className="p-3 bg-slate-50 border-t border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-700">Historial de comisiones · {a.name}
            {credits && <span className="text-slate-500 font-normal ml-2">total crédité : ${credits.total_credited_usd}</span>}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid={`credits-csv-${a.id}`} onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/affiliates/${a.id}/credits.csv`;
              const token = localStorage.getItem("agendik_token");
              fetch(url, { headers: { Authorization: `Bearer ${token}` }}).then(r => r.blob()).then(b => {
                const dl = document.createElement("a"); dl.href = URL.createObjectURL(b);
                dl.download = `agendik-comisiones-${a.code}.csv`; dl.click(); URL.revokeObjectURL(dl.href);
              });
            }} className="text-xs text-blue-600 hover:underline">Exportar CSV</button>
            <button data-testid={`credits-close-${a.id}`} onClick={() => setShowCredits(false)} className="text-slate-400 hover:text-slate-600 text-xs">Fermer ×</button>
          </div>
        </div>
        {!credits ? <p className="text-xs text-slate-500">Chargement…</p> :
         credits.credits.length === 0 ? <p className="text-xs text-slate-500">Aucun crédit pour l'instant.</p> :
         <div className="overflow-x-auto"><table className="w-full text-xs">
           <thead className="text-slate-500"><tr>
             <th className="p-1 text-left">Date</th><th className="p-1 text-left">Type</th>
             <th className="p-1 text-left">Filleul</th><th className="p-1 text-left">Invoice / note</th>
             <th className="p-1 text-right">Montant</th>
           </tr></thead>
           <tbody>{credits.credits.map((c, i) => (
             <tr key={i} className="border-t border-slate-200">
               <td className="p-1">{formatDateShort(c.invoice_paid_at || c.created_at)}</td>
               <td className="p-1">{c.kind === "payout" ? <Badge className="bg-slate-200 text-slate-700 border-slate-300 hover:bg-slate-200 text-[10px]">Payout</Badge>
                                                        : <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 text-[10px]">Crédit</Badge>}</td>
               <td className="p-1">{c.doctor_name || "—"}</td>
               <td className="p-1 font-mono text-[10px] text-slate-500">{c.note || c.invoice_id}</td>
               <td className={`p-1 text-right font-semibold ${c.amount_usd < 0 ? "text-slate-600" : "text-emerald-700"}`}>
                 {c.amount_usd < 0 ? "" : "+"}${c.amount_usd}
               </td>
             </tr>
           ))}</tbody>
         </table></div>}
      </td></tr>
    )}
    </>
  );
}

const SECTIONS = [
  { key: "overview", label: "Vue d'ensemble", icon: TrendingUp },
  { key: "doctors", label: "Médecins", icon: Users },
  { key: "appointments", label: "Rendez-vous", icon: CalendarDays },
  { key: "reviews", label: "Avis à modérer", icon: Star },
  { key: "affiliates", label: "Affiliés", icon: Gift },
  { key: "blocked", label: "Patients bloqués", icon: Ban },
  { key: "visits", label: "Visites", icon: Eye },
  { key: "logs", label: "Logs", icon: ListIcon },
  { key: "settings", label: "Paramètres", icon: SettingsIcon },
];

export default function AdminDashboard() {
  // Section lives in the URL (`/admin/panel/:section`) so the browser back
  // button and refreshes always return to the same admin view.
  const { section: sectionParam } = useParams();
  const nav = useNavigate();
  const VALID_SECTIONS = React.useMemo(() => SECTIONS.map(s => s.key), []);
  const section = VALID_SECTIONS.includes(sectionParam) ? sectionParam : "overview";
  const goSection = (key) => nav(`/admin/panel/${key}`);

  const [rev, setRev] = useState(null);
  const [docs, setDocs] = useState([]);
  const [apts, setApts] = useState([]);
  const [revs, setRevs] = useState([]);
  const [affs, setAffs] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [settings, setSettings] = useState({venezuela_enabled: false});
  const [affForm, setAffForm] = useState({ name: "", email: "", code: "" });
  const [blockEmail, setBlockEmail] = useState("");
  // Reviews filters (iter14 Phase 3)
  const [reviewMode, setReviewMode] = useState("published"); // published | hidden
  const [reviewFilterDoctor, setReviewFilterDoctor] = useState("");

  // Doc viewer modal state kept in URL so the browser back button (and mobile
  // back gesture) CLOSES the modal instead of leaving the admin panel.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewerKind = searchParams.get("doc");
  const viewerDoctorId = searchParams.get("doctorId");
  const viewerOpen = !!(viewerKind && viewerDoctorId);
  const viewerDoctor = viewerOpen ? docs.find(d => d.id === viewerDoctorId) : null;
  const openDoc = (d, kind) => {
    const sp = new URLSearchParams(searchParams);
    sp.set("doc", kind); sp.set("doctorId", d.id);
    setSearchParams(sp);
  };
  const closeDoc = () => {
    if (!viewerOpen) return; // idempotent: back button already cleared params
    const sp = new URLSearchParams(searchParams);
    sp.delete("doc"); sp.delete("doctorId");
    // replace: don't stack an empty-params history entry on top of the open one,
    // otherwise the browser back button would re-open the modal.
    setSearchParams(sp, { replace: true });
  };

  const load = () => {
    api.get("/admin/revenue").then(r => setRev(r.data));
    api.get("/admin/doctors").then(r => setDocs(r.data));
    api.get("/admin/appointments").then(r => setApts(r.data));
    api.get("/admin/reviews").then(r => setRevs(r.data));
    api.get("/admin/affiliates").then(r => setAffs(r.data));
    api.get("/admin/blocked-patients").then(r => setBlocked(r.data));
    api.get("/admin/settings").then(r => setSettings(r.data));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id) => { await api.post(`/admin/doctors/${id}/toggle`); toast.success("Statut mis à jour"); load(); };
  const sendReset = async (id, email) => {
    if (!confirm(`Envoyer un lien de réinitialisation à ${email} ?`)) return;
    try {
      await api.post(`/admin/doctors/${id}/send-reset-link`);
      toast.success("Lien de réinitialisation envoyé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const approve = async (id, ok) => {
    try {
      await api.post(`/admin/doctors/${id}/approve`, { approved: ok });
      toast.success(ok ? "Approuvé" : "Rejeté");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };
  const moderate = async (rid) => { await api.post(`/admin/reviews/${rid}/moderate`); toast.success("Avis masqué"); reloadReviews(); };
  const unhideReview = async (rid) => { await api.post(`/admin/reviews/${rid}/unhide`); toast.success("Avis réaffiché"); reloadReviews(); };
  const reloadReviews = () => {
    const params = new URLSearchParams();
    params.set("hidden", reviewMode === "hidden" ? "true" : "false");
    if (reviewFilterDoctor) params.set("doctor_id", reviewFilterDoctor);
    api.get(`/admin/reviews?${params.toString()}`).then(r => setRevs(r.data));
  };
  useEffect(() => { reloadReviews(); /* eslint-disable-next-line */ }, [reviewMode, reviewFilterDoctor]);
  const createAff = async () => {
    if (!affForm.name || !affForm.email || !affForm.code) return toast.error("Champs requis");
    await api.post("/admin/affiliates", affForm); setAffForm({name:"",email:"",code:""}); toast.success("Affilié créé"); load();
  };
  const markPaid = async (code) => { await api.post(`/admin/affiliates/${code}/mark-paid`); toast.success("Payé"); load(); };
  const blockPatient = async () => {
    if (!blockEmail) return; await api.post(`/admin/patients/block`, null, { params: { email: blockEmail }});
    setBlockEmail(""); toast.success("Patient bloqué"); load();
  };
  const unblockPatient = async (email) => { await api.post(`/admin/patients/unblock`, null, { params: { email }}); toast.success("Débloqué"); load(); };
  const toggleVz = async (v) => { const r = await api.post(`/admin/settings/venezuela`, null, { params: { enabled: v }}); setSettings(r.data); toast.success("Paramètre enregistré"); };
  const toggleTest = async (id, isTest) => {
    try {
      await api.put(`/admin/doctors/${id}/test-flag`, { is_test: isTest });
      toast.success(isTest ? "Marqué comme TEST" : "Marqué comme réel");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const exportCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/affiliates/export.csv`;
    const token = localStorage.getItem("agendik_token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` }}).then(r => r.blob()).then(b => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(b);
      a.download = "affiliates.csv"; a.click();
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10">
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">Tableau d'administration</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
        <aside className="lg:sticky lg:top-20 h-fit">
          <Card><CardContent className="p-2">
            {SECTIONS.map(s => (
              <button key={s.key} data-testid={`admin-nav-${s.key}`} onClick={() => goSection(s.key)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${section === s.key ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-50"}`}>
                <s.icon size={16}/> {s.label}
              </button>
            ))}
          </CardContent></Card>
        </aside>

        <div className="space-y-6">
          <ErrorBoundary>
          {section === "overview" && rev && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat icon={DollarSign} label="MRR" value={`$${rev.monthly_recurring_revenue.toFixed(0)}`} tone="bg-emerald-50 text-emerald-600"/>
                <Stat icon={TrendingUp} label="Total collecté" value={`$${rev.total_collected_revenue.toFixed(0)}`} tone="bg-blue-50 text-blue-600"/>
                <Stat icon={Users} label="Médecins" value={rev.total_doctors} tone="bg-purple-50 text-purple-600"/>
                <Stat icon={CalendarDays} label="RDV" value={rev.total_appointments} tone="bg-amber-50 text-amber-600"/>
              </div>
              <Card><CardContent className="p-5">
                <h2 className="font-semibold text-slate-900 mb-3">En attente de vérification</h2>
                {docs.filter(d => d.status === "pending").length === 0 ? <p className="text-sm text-slate-500">Aucun.</p> :
                  <div className="space-y-3">{docs.filter(d => d.status === "pending").map(d => {
                    const missing = [];
                    if (!d.cedula_front_url) missing.push("cédula recto");
                    if (!d.cedula_back_url) missing.push("cédula verso");
                    if (!(d.senescyt_number || "").trim()) missing.push("N° SENESCYT");
                    if (!d.diploma_url) missing.push("titre");
                    const canApprove = missing.length === 0;
                    return (
                    <div key={d.id} className="p-3 rounded-xl border border-amber-200 bg-amber-50">
                      <div className="flex flex-col gap-2">
                        <div>
                          <div className="font-medium text-slate-900">{drName(d.full_name)}</div>
                          <div data-testid={`pending-senescyt-${d.id}`} className="text-xs text-slate-600 font-mono">
                            SENESCYT&nbsp;: {d.senescyt_number || "—"} · {d.city}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button data-testid={`view-cedula-front-${d.id}`} size="sm" variant="outline"
                                  disabled={!d.cedula_front_url}
                                  onClick={() => openDoc(d, "cedula_front")}
                                  className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-40">
                            <IdCard size={12} className="mr-1"/> Ver cédula (recto)
                          </Button>
                          <Button data-testid={`view-cedula-back-${d.id}`} size="sm" variant="outline"
                                  disabled={!d.cedula_back_url}
                                  onClick={() => openDoc(d, "cedula_back")}
                                  className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-40">
                            <IdCard size={12} className="mr-1"/> Ver cédula (reverso)
                          </Button>
                          <Button data-testid={`view-diploma-${d.id}`} size="sm" variant="outline"
                                  disabled={!d.diploma_url}
                                  onClick={() => openDoc(d, "diploma")}
                                  className="h-8 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-40">
                            <FileText size={12} className="mr-1"/> Ver título
                          </Button>
                          <a data-testid={`senescyt-check-${d.id}`} href={SENESCYT_URL}
                             target="_blank" rel="noopener noreferrer"
                             onClick={(e) => openSenescyt(e, d.senescyt_number)}
                             className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs font-medium">
                            <ExternalLink size={12}/> Verificar en SENESCYT
                          </a>
                          <Button data-testid={`approve-${d.id}`} size="sm"
                                  onClick={() => approve(d.id, true)}
                                  disabled={!canApprove}
                                  title={canApprove ? "Approuver" : `Documents manquants : ${missing.join(", ")}`}
                                  className="bg-emerald-600 hover:bg-emerald-700 h-8 disabled:opacity-40 disabled:cursor-not-allowed">
                            Approuver
                          </Button>
                          <Button data-testid={`reject-${d.id}`} size="sm" variant="outline" onClick={() => approve(d.id, false)}
                                  className="border-red-200 text-red-600 hover:bg-red-50 h-8">Rejeter</Button>
                        </div>
                        {!canApprove && (
                          <div data-testid={`pending-missing-${d.id}`} className="text-[11px] text-amber-800 bg-amber-100 rounded px-2 py-1">
                            Documents manquants pour l'approbation&nbsp;: <strong>{missing.join(", ")}</strong>
                          </div>
                        )}
                      </div>
                    </div>);
                  })}
                  </div>}
              </CardContent></Card>
            </>
          )}

          {section === "doctors" && (
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[960px] text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left p-3">Médecin</th><th className="text-left p-3">Ville</th>
                  <th className="text-left p-3">SENESCYT</th><th className="text-left p-3">Statut</th>
                  <th className="text-left p-3">Abonnement</th><th className="text-left p-3">Actif</th>
                  <th className="text-left p-3">Actions</th></tr>
                </thead>
                <tbody>
                {docs.map(d => {
                    const rowMissing = [];
                    if (!d.cedula_front_url) rowMissing.push("cédula recto");
                    if (!d.cedula_back_url) rowMissing.push("cédula verso");
                    if (!(d.senescyt_number || "").trim()) rowMissing.push("N° SENESCYT");
                    if (!d.diploma_url) rowMissing.push("titre");
                    const rowCanApprove = rowMissing.length === 0;
                    return (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="p-3">
                        <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                          <span>{drName(d.full_name)}</span>
                          {d.is_test && <Badge data-testid={`test-badge-${d.id}`} className="bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-100">TEST</Badge>}
                        </div>
                        <div className="text-xs text-slate-500">{d.email}</div>
                      </td>
                      <td className="p-3">{d.city}</td>
                      <td className="p-3 text-xs font-mono">{d.senescyt_number || "—"}</td>
                      <td className="p-3">
                        {d.status === "pending" && <Badge data-testid={`status-pending-${d.id}`} className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">En attente</Badge>}
                        {d.status === "approved" && <Badge data-testid={`status-approved-${d.id}`} className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Approuvé</Badge>}
                        {d.status === "rejected" && <Badge data-testid={`status-rejected-${d.id}`} className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">Rejeté</Badge>}
                      </td>
                      <td className="p-3">
                        {d.subscription_status === "active" && <Badge data-testid={`sub-active-${d.id}`} className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">Actif</Badge>}
                        {(d.subscription_status === "trial" || d.subscription_status === "trialing") && (() => {
                          // "Essai (X jours restants)" — one badge, computed from trial_ends_at.
                          const daysLeft = d.trial_ends_at
                            ? Math.max(0, Math.ceil((new Date(d.trial_ends_at) - new Date()) / 86400000))
                            : null;
                          const label = daysLeft !== null ? `Essai (${daysLeft} jours restants)` : "Essai";
                          return (
                            <Badge data-testid={`sub-trial-${d.id}`} className="bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-100">
                              {label}
                            </Badge>
                          );
                        })()}
                        {d.subscription_status === "paused" && <Badge data-testid={`sub-paused-${d.id}`} className="bg-slate-200 text-slate-700 border-slate-300 hover:bg-slate-200">En pause</Badge>}
                        {d.subscription_status === "past_due" && <Badge data-testid={`sub-pastdue-${d.id}`} className="bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-100">Paiement en retard</Badge>}
                        {d.subscription_status === "unpaid" && <Badge data-testid={`sub-unpaid-${d.id}`} className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">Impayé</Badge>}
                        {d.subscription_status === "cancelled" && <Badge data-testid={`sub-cancelled-${d.id}`} variant="outline" className="text-slate-600">Annulé</Badge>}
                        {d.subscription_status === "incomplete" && <Badge variant="outline" className="text-amber-700 border-amber-200">Incomplet</Badge>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Switch data-testid={`admin-toggle-${d.id}`} checked={d.active} onCheckedChange={() => toggle(d.id)}/>
                          <label className="flex items-center gap-1 text-[11px] cursor-pointer text-purple-700" title="Compte de test">
                            <input type="checkbox" data-testid={`test-toggle-${d.id}`}
                                   checked={!!d.is_test}
                                   onChange={e => toggleTest(d.id, e.target.checked)}
                                   className="w-3.5 h-3.5 accent-purple-600"/>
                            Test
                          </label>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <a data-testid={`senescyt-link-${d.id}`} href={SENESCYT_URL}
                             target="_blank" rel="noopener noreferrer"
                             onClick={(e) => openSenescyt(e, d.senescyt_number)}
                             className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                            <ExternalLink size={11}/> SENESCYT
                          </a>
                          {d.cedula_front_url && (
                            <button data-testid={`cedula-front-link-${d.id}`} type="button"
                                    onClick={() => openDoc(d, "cedula_front")}
                                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                              <IdCard size={11}/> Cédula rec.
                            </button>
                          )}
                          {d.cedula_back_url && (
                            <button data-testid={`cedula-back-link-${d.id}`} type="button"
                                    onClick={() => openDoc(d, "cedula_back")}
                                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                              <IdCard size={11}/> Cédula rev.
                            </button>
                          )}
                          {d.diploma_url ? (
                            <button data-testid={`diploma-link-${d.id}`} type="button"
                                    onClick={() => openDoc(d, "diploma")}
                                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                              <FileText size={11}/> Título
                            </button>
                          ) : <span className="text-xs text-slate-400">Aucun titre</span>}
                          {d.photo_url ? (
                            <button data-testid={`photo-link-${d.id}`} type="button"
                                    onClick={() => openDoc(d, "photo")}
                                    className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline">
                              <ImageIcon size={11}/> Foto
                            </button>
                          ) : <span className="text-xs text-slate-400">Aucune photo</span>}
                          {d.status === "pending" && (
                            <>
                              <Button data-testid={`row-approve-${d.id}`} size="sm" onClick={() => approve(d.id, true)}
                                      disabled={!rowCanApprove}
                                      title={rowCanApprove ? "Approuver" : `Documents manquants : ${rowMissing.join(", ")}`}
                                      className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed">Approuver</Button>
                              <Button data-testid={`row-reject-${d.id}`} size="sm" variant="outline" onClick={() => approve(d.id, false)}
                                      className="border-red-200 text-red-600 hover:bg-red-50 h-7 px-2 text-xs">Rejeter</Button>
                            </>
                          )}
                          {d.status === "rejected" && (
                            <Button data-testid={`row-reapprove-${d.id}`} size="sm" onClick={() => approve(d.id, true)}
                                    disabled={!rowCanApprove}
                                    title={rowCanApprove ? "Ré-approuver" : `Documents manquants : ${rowMissing.join(", ")}`}
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs disabled:opacity-40 disabled:cursor-not-allowed">Ré-approuver</Button>
                          )}
                          <Button data-testid={`send-reset-${d.id}`} size="sm" variant="outline"
                                  onClick={() => sendReset(d.id, d.email)}
                                  className="h-7 px-2 text-xs border-slate-200 text-slate-600 hover:bg-slate-50">
                            Envoyer un lien
                          </Button>
                        </div>
                      </td>
                    </tr>);
                  })}
                </tbody>
              </table>
            </CardContent></Card>
          )}

          {section === "appointments" && (
            <Card><CardContent className="p-0 overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Médecin</th>
                  <th className="text-left p-3">Patient</th><th className="text-left p-3">Statut</th></tr>
                </thead>
                <tbody>
                  {apts.slice(0, 100).map(a => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="p-3">{formatDateTimeParts(a.date, a.time)}</td>
                      <td className="p-3">{drName(a.doctor_name)}</td>
                      <td className="p-3">{a.patient_name} <span className="text-xs text-slate-400">· {a.patient_email}</span></td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{statusLabel(a.status, "fr")}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent></Card>
          )}

          {section === "reviews" && (
            <Card><CardContent className="p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                  <button data-testid="reviews-mode-published" onClick={() => setReviewMode("published")}
                          className={`px-3 py-1.5 text-xs font-medium ${reviewMode === "published" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>Avis publiés / à modérer</button>
                  <button data-testid="reviews-mode-hidden" onClick={() => setReviewMode("hidden")}
                          className={`px-3 py-1.5 text-xs font-medium ${reviewMode === "hidden" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>Avis masqués</button>
                </div>
                <select data-testid="reviews-doctor-filter" value={reviewFilterDoctor}
                        onChange={e => setReviewFilterDoctor(e.target.value)}
                        className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white">
                  <option value="">Tous les médecins</option>
                  {docs.map(d => <option key={d.id} value={d.id}>{drName(d.full_name)} · {d.specialty}</option>)}
                </select>
              </div>
              {revs.length === 0 ? <p className="text-sm text-slate-500">Aucun avis.</p> :
                revs.map(r => (
                  <div key={r.id} data-testid={`review-${r.id}`} className="p-3 rounded-xl border border-slate-200">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">
                          {"⭐".repeat(r.rating)}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          <span className="font-medium">{r.doctor_title || "Dr."} {r.doctor_full_name}</span>
                          <span className="text-slate-400"> · {r.doctor_specialty}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          Patient : <span className="font-medium">{r.patient_name}</span>
                          <span className="text-slate-400"> · {formatDateShort(r.created_at)}</span>
                        </div>
                      </div>
                      {reviewMode === "published" ? (
                        <Button data-testid={`moderate-${r.id}`} size="sm" variant="outline"
                                onClick={() => moderate(r.id)}
                                className="border-red-200 text-red-600 hover:bg-red-50 h-7 text-xs">Masquer</Button>
                      ) : (
                        <div className="flex flex-col items-end gap-1">
                          <Button data-testid={`unhide-${r.id}`} size="sm" variant="outline"
                                  onClick={() => unhideReview(r.id)}
                                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-7 text-xs">Réafficher</Button>
                          {r.moderated_at && <span className="text-[10px] text-slate-400">Masqué le {formatDateShort(r.moderated_at)}</span>}
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 mt-2">{r.comment || <em className="text-slate-400">(sans commentaire)</em>}</p>
                  </div>
                ))}
            </CardContent></Card>
          )}

          {section === "affiliates" && (
            <>
              <Card><CardContent className="p-5">
                <h2 className="font-semibold mb-3">Demandes en attente</h2>
                {(affs.filter(a => a.type === "partner" && a.status === "pending").length === 0) ? (
                  <p data-testid="no-pending-partners" className="text-xs text-slate-500">Aucune demande en attente.</p>
                ) : (
                  <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                      <tr><th className="p-2 text-left">Partenaire</th><th className="p-2 text-left">Contact</th>
                      <th className="p-2 text-left">Paiement</th><th className="p-2 text-left">Reçu</th>
                      <th className="p-2 text-left">Actions</th></tr>
                    </thead>
                    <tbody>{affs.filter(a => a.type === "partner" && a.status === "pending").map(a => (
                      <PartnerPendingRow key={a.id} a={a} onDone={load}/>
                    ))}</tbody></table></div>
                )}
              </CardContent></Card>

              <Card><CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold">Partenaires actifs</h2>
                  <div className="flex gap-2">
                    <Button data-testid="accrue-now-btn" size="sm" variant="outline" onClick={async () => {
                      try { const r = await api.post("/admin/affiliates/accrue-now"); toast.success(`Réconciliation OK · ${r.data.newly_credited} nouveaux crédits`); load(); }
                      catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                    }}>Réconcilier Stripe</Button>
                    <Button data-testid="export-csv-btn" size="sm" variant="outline" onClick={exportCsv}>Exporter CSV</Button>
                  </div>
                </div>
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr><th className="p-2 text-left">Code</th><th className="p-2 text-left">Partenaire</th>
                    <th className="p-2 text-right">Inscrits</th><th className="p-2 text-right">Confirmés</th>
                    <th className="p-2 text-right">Solde</th><th className="p-2 text-right">Payé cumul.</th>
                    <th className="p-2 text-right">Actions</th></tr>
                  </thead>
                  <tbody>{affs.filter(a => (a.status === "approved" || !a.type) && a.code).map(a => (
                    <PartnerActiveRow key={a.id || a.code} a={a} onDone={load}/>
                  ))}</tbody></table></div>
              </CardContent></Card>

              <Card><CardContent className="p-5">
                <h2 className="font-semibold mb-3">Créer un affilié manuellement</h2>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                  <Input placeholder="Nom" value={affForm.name} onChange={e => setAffForm({...affForm, name: e.target.value})}/>
                  <Input placeholder="Email" value={affForm.email} onChange={e => setAffForm({...affForm, email: e.target.value})}/>
                  <Input placeholder="CODE" value={affForm.code} onChange={e => setAffForm({...affForm, code: e.target.value.toUpperCase()})}/>
                  <Button data-testid="create-aff-btn" onClick={createAff} className="bg-blue-600 hover:bg-blue-700">Créer</Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Commission : $9 × médecins confirmés (au moins un paiement réussi et abonnement à jour).</p>
              </CardContent></Card>
            </>
          )}

          {section === "blocked" && (
            <Card><CardContent className="p-5 space-y-4">
              <div className="flex gap-2 max-w-md">
                <Input placeholder="email@patient.com" value={blockEmail} onChange={e => setBlockEmail(e.target.value)}/>
                <Button data-testid="block-btn" onClick={blockPatient} className="bg-red-600 hover:bg-red-700">Bloquer globalement</Button>
              </div>
              {blocked.length === 0 ? <p className="text-sm text-slate-500">Aucun patient bloqué.</p> :
                <div className="space-y-2">{blocked.map(b => (
                  <div key={b.patient_email} className="flex items-center justify-between p-2 rounded-lg border">
                    <span className="text-sm">{b.patient_email}</span>
                    <Button data-testid={`unblock-${b.patient_email}`} size="sm" variant="outline" onClick={() => unblockPatient(b.patient_email)} className="h-7">Débloquer</Button>
                  </div>))}
                </div>}
            </CardContent></Card>
          )}

          {section === "visits" && <AdminVisitsSection/>}
          {section === "logs" && <AdminLogsSection/>}

          {section === "settings" && (
            <Card><CardContent className="p-5 space-y-6">
              <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200">
                <div>
                  <div className="font-semibold text-slate-900">Marché Venezuela</div>
                  <div className="text-xs text-slate-500">Réactive Caracas / Venezuela dans les listes.</div>
                </div>
                <Switch data-testid="venezuela-toggle" checked={!!settings?.venezuela_enabled} onCheckedChange={toggleVz}/>
              </div>

              <TestAccountsCard/>
            </CardContent></Card>
          )}
          </ErrorBoundary>
        </div>
      </div>
      <DocViewerDialog open={viewerOpen}
                       onOpenChange={(o) => { if (!o) closeDoc(); }}
                       doctorId={viewerDoctorId} kind={viewerKind}
                       doctorName={viewerDoctor ? drName(viewerDoctor.full_name) : ""}/>
    </div>
  );
}

function AdminVisitsSection() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [uniqueTotal, setUniqueTotal] = useState(0);
  const [daily, setDaily] = useState([]);
  const [filters, setFilters] = useState({ from: "", to: "", country: "", city: "", page_url: "", page: 1 });
  const load = React.useCallback(async () => {
    const sp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) sp.set(k, v); });
    try {
      const r = await api.get(`/admin/visits?${sp.toString()}`);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setUniqueTotal(r.data.unique_total || 0);
      setDaily(r.data.daily || []);
    } catch { setRows([]); setTotal(0); setUniqueTotal(0); setDaily([]); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card><CardContent className="p-5">
      <h2 className="font-semibold text-slate-900 mb-3">Visites</h2>
      {/* Two headline counters: unique visitors (1 IP/day via one-way salted
          fingerprint) and page views (every /api/visits insertion). */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div data-testid="visits-unique-counter" className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Visiteurs uniques</div>
          <div className="text-2xl font-bold text-blue-900 mt-1">{uniqueTotal.toLocaleString("fr-FR")}</div>
          <div className="text-[10px] text-slate-500 mt-1">1 IP = 1 visite / jour</div>
        </div>
        <div data-testid="visits-pageviews-counter" className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Pages vues</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{total.toLocaleString("fr-FR")}</div>
          <div className="text-[10px] text-slate-500 mt-1">Toutes les pages chargées</div>
        </div>
      </div>
      {daily.length > 0 && (
        <div className="flex gap-2 overflow-x-auto mb-4 pb-2">
          {daily.map(d => (
            <div key={d.day} className="shrink-0 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
              <div className="text-slate-500">{d.day}</div>
              <div className="font-bold text-blue-700">{d.unique || 0} <span className="text-[10px] text-slate-500 font-normal">uniques</span></div>
              <div className="text-[11px] text-slate-700">{d.count} vues</div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <Input data-testid="visits-from" type="date" value={filters.from} onChange={e => setFilters({...filters, from: e.target.value, page: 1})}/>
        <Input data-testid="visits-to" type="date" value={filters.to} onChange={e => setFilters({...filters, to: e.target.value, page: 1})}/>
        <Input data-testid="visits-country" placeholder="País" value={filters.country} onChange={e => setFilters({...filters, country: e.target.value, page: 1})}/>
        <Input data-testid="visits-city" placeholder="Ciudad" value={filters.city} onChange={e => setFilters({...filters, city: e.target.value, page: 1})}/>
        <Input data-testid="visits-page" placeholder="Page" value={filters.page_url} onChange={e => setFilters({...filters, page_url: e.target.value, page: 1})}/>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="visits-table" className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase">
            <tr><th className="p-2 text-left">Date (EC)</th><th className="p-2 text-left">Page</th><th className="p-2 text-left">Device</th><th className="p-2 text-left">IP</th><th className="p-2 text-left">Pays/Ville</th><th className="p-2 text-left">Source</th></tr>
          </thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="p-2 whitespace-nowrap">{r.at_ec || ""}</td>
              <td className="p-2 font-mono">{r.page || ""}</td>
              <td className="p-2">{r.device || ""}</td>
              <td className="p-2 font-mono">{r.ip_masked || ""}</td>
              <td className="p-2">{[r.country, r.city].filter(Boolean).join(" · ")}</td>
              <td className="p-2 truncate max-w-[180px]">{r.utm_source || r.ref || r.referrer || ""}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 mt-3 text-xs">
        <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({...filters, page: filters.page - 1})}>←</Button>
        <span>Page {filters.page}</span>
        <Button size="sm" variant="outline" disabled={rows.length < 50} onClick={() => setFilters({...filters, page: filters.page + 1})}>→</Button>
      </div>
    </CardContent></Card>
  );
}

function AdminLogsSection() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ type: "", level: "", from: "", to: "", page: 1 });
  const load = React.useCallback(async () => {
    const sp = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) sp.set(k, v); });
    try { const r = await api.get(`/admin/logs?${sp.toString()}`); setRows(r.data.rows || []); }
    catch { setRows([]); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);
  return (
    <Card><CardContent className="p-5">
      <h2 className="font-semibold text-slate-900 mb-3">Logs</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Input data-testid="logs-type" placeholder="Type" value={filters.type} onChange={e => setFilters({...filters, type: e.target.value, page: 1})}/>
        <Input data-testid="logs-level" placeholder="info / erreur" value={filters.level} onChange={e => setFilters({...filters, level: e.target.value, page: 1})}/>
        <Input data-testid="logs-from" type="date" value={filters.from} onChange={e => setFilters({...filters, from: e.target.value, page: 1})}/>
        <Input data-testid="logs-to" type="date" value={filters.to} onChange={e => setFilters({...filters, to: e.target.value, page: 1})}/>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="logs-table" className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500 uppercase">
            <tr><th className="p-2 text-left">Date (EC)</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Niveau</th><th className="p-2 text-left">Message</th><th className="p-2 text-left">Utilisateur</th></tr>
          </thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="p-2 whitespace-nowrap">{r.at_ec || ""}</td>
              <td className="p-2 font-mono">{r.type || ""}</td>
              <td className={`p-2 ${r.level === "erreur" ? "text-rose-600" : "text-slate-700"}`}>{r.level || ""}</td>
              <td className="p-2">{r.message || ""}</td>
              <td className="p-2 truncate max-w-[180px]">{r.user_email || r.user_id || ""}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 mt-3 text-xs">
        <Button size="sm" variant="outline" disabled={filters.page <= 1} onClick={() => setFilters({...filters, page: filters.page - 1})}>←</Button>
        <span>Page {filters.page}</span>
        <Button size="sm" variant="outline" disabled={rows.length < 50} onClick={() => setFilters({...filters, page: filters.page + 1})}>→</Button>
      </div>
    </CardContent></Card>
  );
}
